import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { I18n } from 'react-redux-i18n';
import get from 'lodash/get';

import SelectFilter from 'components/Select/SelectFilter';

import { Wine, Option } from 'interface';
import { FormFilters, OptionsFilters } from 'constants/prices';
import { components } from 'apiTyped/types';

const PriceFiltersDiv = styled.div`
  background-color: rgba(255, 255, 255, 0.75);
  display: grid;
  grid-template-columns: 1fr 1fr 1fr 1fr;
  grid-gap: 30px;
  padding: 30px 20px;
`;

const PriceFilters = ({
  filters,
  onChange,
  disabled = false
}: {
  filters: components['schemas']['PricesFiltersModel']['filters'];
  // eslint-disable-next-line no-unused-vars
  onChange: (e: FormFilters) => void;
  disabled: boolean;
}) => {
  const [wineId, setWineId] = useState<number | undefined>(undefined);
  const [vintage, setVintage] = useState<string | undefined>(undefined);
  const [format, setFormat] = useState<string | undefined>(undefined);

  const options: OptionsFilters = {
    wines: [
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      ...new Set(
        filters.map((item: any) => ({
          id: item.wine_id,
          name: item.wine_name
        }))
      )
    ],
    vintages: get(
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      filters.find((item: any) => item.wine_id === wineId),
      'vintages',
      []
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    ),
    formats: get(
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      filters.find((item: any) => item.wine_id === wineId),
      'formats',
      []
    )
  };

  const selectWine = options.wines.find((wine: Wine) => wine.id === wineId);

  const onChangeWine = (e: { value: number; label: string }) => {
    const updatedFilters = getAdaptedFilters(e.value);
    onChange(updatedFilters);
  };

  const onChangeVintage = (e: Option) => {
    setVintage(e.value);
    const updatedFilters = {
      wineId: wineId,
      vintage: e.value,
      format: format
    };
    onChange(updatedFilters);
  };

  const onChangeFormat = (e: Option) => {
    setFormat(e.value);
    const updatedFilters = {
      wineId: wineId,
      vintage: vintage,
      format: e.value
    };
    onChange(updatedFilters);
  };

  const getAdaptedFilters = (newWineId: number | undefined): FormFilters => {
    setWineId(newWineId);

    if (newWineId === undefined) {
      setFormat(undefined);
      setFormat(undefined);
      return {
        wineId: undefined,
        vintage: undefined,
        format: undefined
      };
    }

    const wineFilters = filters.find(
      (item: components['schemas']['PriceFilter']) => item.wine_id === newWineId
    );

    // updates vintage/format on filters form
    // only if old value not in new selected wine
    let newVintage = vintage;
    if (!(vintage && wineFilters?.vintages.includes(vintage))) {
      newVintage = get(wineFilters, 'vintages[0]', undefined);
      setVintage(newVintage);
    }

    let newFormat = format;
    if (!(format && wineFilters?.formats.includes(format))) {
      newFormat = get(wineFilters, 'formats[0]', undefined);
      setFormat(newFormat);
    }

    return {
      wineId: newWineId,
      vintage: newVintage,
      format: newFormat
    };
  };

  // Set initial wine, vintage and format
  useEffect(() => {
    if (wineId || filters.length == 0) {
      return;
    }

    const wineLocal = filters[0];
    const defaultWineId = get(wineLocal, 'wine_id');
    const initValues = getAdaptedFilters(defaultWineId);
    onChange(initValues);
  }, [filters]);

  return (
    <PriceFiltersDiv>
      <SelectFilter
        label={I18n.t('prices.filters.wine')}
        placeholder={I18n.t('prices.filters.placeholderWine')}
        selectedOption={
          selectWine ? { value: selectWine.id, label: selectWine.name } : null
        }
        options={options.wines.map((wine) => ({
          value: wine.id,
          label: wine.name
        }))}
        onChange={onChangeWine}
        isClearable={false}
        isDisabled={disabled}
      />
      <SelectFilter
        label={I18n.t('prices.filters.vintage')}
        placeholder={I18n.t('prices.filters.placeholderVintage')}
        selectedOption={vintage ? { label: vintage, value: vintage } : null}
        options={options.vintages.map((vintage) => ({
          value: vintage,
          label: vintage
        }))}
        onChange={onChangeVintage}
        isClearable={false}
        isDisabled={disabled}
      />
      <SelectFilter
        label={I18n.t('prices.filters.format')}
        placeholder={I18n.t('prices.filters.placeholderFormat')}
        selectedOption={format ? { label: format, value: format } : null}
        options={options.formats.map((format) => ({
          value: format,
          label: format
        }))}
        onChange={onChangeFormat}
        isClearable={false}
        isDisabled={disabled}
      />
    </PriceFiltersDiv>
  );
};

export default PriceFilters;
